import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import './DesignHero.css'

const API = process.env.REACT_APP_API_URL || ""

// ── Fetch hero image from DB — same pattern as useCompatImages() ─────────────
function useHeroImage() {
    const [url, setUrl] = useState(null)

    useEffect(() => {
        fetch(`${API}/api/media/designhero`)
            .then(res => res.ok ? res.json() : {})
            .then(map => setUrl(map['dh-hero'] || null))
            .catch(() => { })
    }, [])

    return url
}

// ── Image slot — real img if loaded, skeleton while loading ──────────────────
function HeroImageSlot({ url }) {
    if (url) {
        return (
            <div className="dh-img-slot">
                <img
                    src={url}
                    alt="Aviatricks custom UAV airframe"
                    className="dh-hero-img"
                />
            </div>
        )
    }

    // Skeleton while fetching
    return (
        <div className="dh-img-slot dh-img-slot--skeleton">
            <div className="dh-skeleton" />
        </div>
    )
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DesignHero() {
    const wrapRef = useRef(null)
    const eyebrowRef = useRef(null)
    const line1Ref = useRef(null)
    const line2Ref = useRef(null)
    const subRef = useRef(null)
    const badgeRef = useRef(null)
    const imageRef = useRef(null)

    const heroUrl = useHeroImage()

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ defaults: { ease: 'expo.out' } })

            // Image container entrance
            tl.fromTo(imageRef.current,
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 1 },
                0.2
            )

            // Text sequence
            tl.fromTo(eyebrowRef.current,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.7 },
                0.3
            )
            tl.fromTo(line1Ref.current,
                { opacity: 0, y: 70, skewY: 4 },
                { opacity: 1, y: 0, skewY: 0, duration: 1 },
                0.5
            )
            tl.fromTo(line2Ref.current,
                { opacity: 0, y: 70, skewY: 4 },
                { opacity: 1, y: 0, skewY: 0, duration: 1 },
                0.65
            )
            tl.fromTo(subRef.current,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.8 },
                1.0
            )
            tl.fromTo(badgeRef.current,
                { opacity: 0, y: 14 },
                { opacity: 1, y: 0, duration: 0.6 },
                1.2
            )
        }, wrapRef)

        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} className="dh-wrap">

            {/* Circuit trace BG */}
            <svg className="dh-traces" viewBox="0 0 1440 800"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <g stroke="rgba(93,23,158,0.1)" strokeWidth="1" fill="none">
                    <path d="M0,200 H280 V380 H560" strokeDasharray="5 4" />
                    <path d="M0,450 H200 V260 H480 V520 H800" strokeDasharray="5 4" />
                    <path d="M1440,160 H1120 V340 H840" strokeDasharray="5 4" />
                    <path d="M1440,520 H1220 V300 H980" strokeDasharray="5 4" />
                    <path d="M600,800 V620 H820 V440" strokeDasharray="5 4" />
                </g>
                {[[280, 380], [560, 380], [200, 260], [480, 520], [1120, 340], [1220, 300], [820, 440]].map(([x, y], i) => (
                    <g key={i}>
                        <circle cx={x} cy={y} r="4" fill="none"
                            stroke="rgba(93,23,158,0.2)" strokeWidth="1" />
                        <circle cx={x} cy={y} r="1.5" fill="rgba(93,23,158,0.3)" />
                    </g>
                ))}
            </svg>

            {/* Dot grid */}
            <div className="dh-dotgrid" />

            <div className="dh-inner">

                {/* ── Left — text ── */}
                <div className="dh-left">
                    <div ref={eyebrowRef} className="dh-eyebrow" style={{ opacity: 0 }}>
                        <span className="dh-dot" />
                        AVIATRICKS AERO LABS
                        <span className="dh-slash">/</span>
                        DESIGN
                    </div>

                    <h1 className="dh-title">
                        <span ref={line1Ref} className="dh-line" style={{ opacity: 0 }}>
                            AIRFRAME
                        </span>
                        <span ref={line2Ref} className="dh-line dh-line--accent" style={{ opacity: 0 }}>
                            DESIGN.
                        </span>
                    </h1>

                    <p ref={subRef} className="dh-sub" style={{ opacity: 0 }}>
                        Custom UAV airframes engineered in-house —
                        from geometry and aerodynamic layout to
                        flight-ready structure.
                    </p>

                    <div ref={badgeRef} className="dh-badges" style={{ opacity: 0 }}>
                        <span className="dh-badge">
                            <span className="dh-badge-dot" />IN-HOUSE ENGINEERING
                        </span>
                        <span className="dh-badge dh-badge--soon">COMING SOON</span>
                    </div>
                </div>

                {/* ── Right — hero image from DB ── */}
                <div ref={imageRef} className="dh-right" style={{ opacity: 0 }}>
                    <HeroImageSlot url={heroUrl} />
                </div>

            </div>

            {/* Bottom bar */}
            <div className="dh-bottom-bar">
                <span className="dh-mono">AVIATRICKS</span>
                <span className="dh-bar-line" />
                <span className="dh-mono">INDIGENOUS UAV DESIGN</span>
                <span className="dh-bar-line" />
                <span className="dh-mono dh-blink">● MADE IN INDIA</span>
            </div>

        </section>
    )
}